import React from 'react';
import DataTable, { ColumnDef } from '../utils/DataTable';
import { Surface, Meter, SignedValue, Pill, chartSeries } from '@/components/ui';
import { sectorColor } from '@/theme/chartColors';
import { formatPercentage } from '@/utils/formatters';
import styles from './ClientPerformance.module.css';

// Shared building blocks for the performance report pages (ClientPerformance
// and the IFSC report). Extracted so both layouts stay visually identical.

// No-op for DataTable's required paging/sort callbacks (uses `undefined` rather than an
// empty body to satisfy the no-empty-function lint rule).
const NOOP = () => undefined;

/**
 * One results section — the standard card, so it matches every other card in the app.
 * `subtitle` sits under the heading (an explanation of the figures); `meta` sits
 * on the right of the card head (a count, a date).
 */
export const SectionCard: React.FC<{
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    meta?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, subtitle, meta, children }) => (
    <Surface
        // Rows of cards line up: a short card stretches to its neighbour's height.
        className={styles.card}
        // Surface renders the title inside an <h2>, which takes phrasing content — spans only.
        title={
            <span className={styles.cardTitle}>
                {title}
                {subtitle && <span className={styles.cardSubtitle}>{subtitle}</span>}
            </span>
        }
        meta={meta}
    >
        {children}
    </Surface>
);

/**
 * A static table inside a section card: no search, paging or sorting.
 *
 * `embedded` is what makes it chrome-less — it drops DataTable's own page frame
 * and card so the surrounding SectionCard is the only surface. It used to render
 * standalone and undo the wrapper with descendant selectors, which also reset the
 * table's typeface away from the theme.
 */
export function StaticTable<T>({ data, columns, rowKey, maxBodyHeight, title, loading = false, emptyMessage = "No data." }: {
    data: T[];
    columns: ColumnDef<T>[];
    rowKey: (row: T, index: number) => string | number;
    /** Scroll the rows past this height (header stays pinned) — for long lists. */
    maxBodyHeight?: string;
    title?: string;
    loading?: boolean;
    emptyMessage?: string;
}) {
    const table = (
        <DataTable
            embedded
            title={title}
            data={data}
            columns={columns}
            rowKey={rowKey}
            totalCount={data.length}
            loading={loading}
            error={null}
            currentPage={1}
            totalPages={1}
            onPageChange={NOOP}
            pageSize={data.length || 1}
            sortField=""
            sortDir="asc"
            onSort={NOOP}
            hideSearch
            hideFooter
            compact
            emptyMessage={emptyMessage}
            maxBodyHeight={maxBodyHeight}
        />
    );
    // A capped table scrolls, so give it the app's slim scrollbar.
    return maxBodyHeight ? <div className={styles.thinScroll}>{table}</div> : table;
}

export interface SummaryItem {
    label: string;
    value: React.ReactNode;
    /** The headline figure — spans the full width, larger type. */
    hero?: boolean;
}

/**
 * The report's key figures as a label/value grid. Replaces a two-column
 * "Metric / Value" table, which buried the portfolio value among its inputs.
 */
export const SummaryGrid: React.FC<{ items: SummaryItem[] }> = ({ items }) => (
    <dl className={styles.summary}>
        {items.map(item => (
            <div key={item.label} className={`${styles.summaryCell}${item.hero ? ` ${styles.summaryHero}` : ''}`}>
                <dt className={styles.summaryLabel}>{item.label}</dt>
                <dd className={styles.summaryValue}>{item.value}</dd>
            </div>
        ))}
    </dl>
);

/** A period return: green when positive, red when negative. */
export const ReturnCell: React.FC<{ value: number | null | undefined }> = ({ value }) => (
    <SignedValue value={value} format={formatPercentage} />
);

/** A benchmark figure, deliberately quieter than the portfolio's own return. */
export const BenchmarkCell: React.FC<{ value: number | null | undefined }> = ({ value }) => (
    <span className={styles.benchmark}>{formatPercentage(value)}</span>
);

/** Height at which the sector and holdings lists start scrolling (~12 rows). */
export const LONG_LIST_MAX_HEIGHT = '600px';

/** Largest share in a set of rows — the Meter scales against it so small weights stay visible. */
export const maxShare = (rows: Array<{ percentage: string | number }> | undefined): number =>
    (rows ?? []).reduce((max, row) => Math.max(max, Number(row.percentage) || 0), 0);

/** A weight column: the exact percentage plus a share bar (neutral — a large weight isn't "good"). */
export const ShareCell: React.FC<{ value: string | number; max: number }> = ({ value, max }) => (
    <Meter neutral value={Number(value) || 0} max={max} label={formatPercentage(value)} />
);

/** The heading above a loaded report: who it is for and the as-on date. */
export const ReportHeading: React.FC<{
    code?: React.ReactNode;
    name: React.ReactNode;
    badge?: React.ReactNode;
    asOf: React.ReactNode;
}> = ({ code, name, badge, asOf }) => (
    <div className={styles.reportHead}>
        {code && <span className={styles.reportCode}>{code}</span>}
        <h2 className={styles.reportName}>{name}</h2>
        {badge}
        <span className={styles.reportAsOf}>
            <Pill solid plain>as of {asOf}</Pill>
        </span>
    </div>
);

export interface AllocationSlice {
    label: string;
    percentage: string | number;
    /** Formatted market value; omitted where only weights are known. */
    value?: React.ReactNode;
}

// Colour follows the slice's position in the backend's order — never its rank —
// so the same asset class keeps its colour from one report to the next.
const sliceColor = (index: number) => (index < chartSeries.length ? chartSeries[index] : sectorColor(index));

/**
 * Portfolio composition: one 100% bar showing the mix at a glance, then a
 * legend that carries every figure (so identity is never colour alone).
 */
export const AllocationBreakdown: React.FC<{ slices: AllocationSlice[] }> = ({ slices }) => {
    if (slices.length === 0) return <p className={styles.allocEmpty}>No data.</p>;
    return (
        <div className={styles.alloc}>
            <div className={styles.allocBar} role="img" aria-label="Allocation by asset class">
                {slices.map((slice, index) => (
                    <span
                        key={slice.label}
                        className={styles.allocSegment}
                        style={{ flexGrow: Math.max(Number(slice.percentage) || 0, 0), background: sliceColor(index) }}
                        title={`${slice.label}: ${formatPercentage(slice.percentage)}`}
                    />
                ))}
            </div>
            <ul className={styles.allocList}>
                {slices.map((slice, index) => (
                    <li key={slice.label} className={styles.allocRow}>
                        <span className={styles.allocSwatch} style={{ background: sliceColor(index) }} />
                        <span className={styles.allocLabel}>{slice.label}</span>
                        {slice.value !== undefined && <span className={styles.allocValue}>{slice.value}</span>}
                        <span className={styles.allocPct}>{formatPercentage(slice.percentage)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};
